/**
 * Sayi / zaman bicimlendirme - TEK YER.
 *
 * SAAT DILIMI SOZLESMESI
 *   Backend her zamani UTC epoch ms olarak tasir (mum KAPANIS ani).
 *   Kullaniciya gosterim daima Turkiye saatiyle (`Europe/Istanbul`)
 *   yapilir ve bu donusum YALNIZCA burada olur. Bilesenlerde
 *   `new Date(...).toLocaleString()` cagrilmaz; aksi halde gosterim
 *   makinenin saat dilimine gore degisir ve "22:15" ile "19:15"
 *   karisir.
 *
 *   Gosterilen zaman MUM KAPANIS zamanidir; olay (event) zamani veya
 *   mum acilis zamani DEGILDIR.
 */

const LOCALE = "tr-TR";

/** Kullaniciya gosterilen saat dilimi. Tek dogruluk kaynagi. */
export const DISPLAY_TIME_ZONE = "Europe/Istanbul";

/** Kisa etiket - ekranda saatin hangi dilimde oldugunu belirtmek icin. */
export const DISPLAY_TIME_ZONE_LABEL = "TSİ";

/** Fiyat: buyuklugune gore anlamli basamak sayisi secilir. */
export function price(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—";
  const abs = Math.abs(value);
  const digits = abs >= 1000 ? 2 : abs >= 1 ? 4 : abs >= 0.01 ? 6 : 8;
  return value.toLocaleString(LOCALE, {
    minimumFractionDigits: 2,
    maximumFractionDigits: digits,
  });
}

export function pct(value: number | null | undefined, digits = 2): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return `${value >= 0 ? "+" : ""}${value.toFixed(digits)}%`;
}

/** Isaretsiz yuzde (mesafe, oran gibi degerler icin). */
export function pctPlain(value: number | null | undefined, digits = 2): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return `%${value.toFixed(digits)}`;
}

/** Buyuk tutarlari kisaltir: 1.2B / 340M / 12.5K */
export function compact(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—";
  const abs = Math.abs(value);
  if (abs >= 1e9) return `${(value / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${(value / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${(value / 1e3).toFixed(1)}K`;
  return value.toFixed(2);
}

export function money(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return value.toLocaleString(LOCALE, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function ratio(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return `${value.toFixed(2)}x`;
}

/* ==========================================================================
   Zaman - hepsi Europe/Istanbul
   ========================================================================== */
function fmt(epochMs: number, options: Intl.DateTimeFormatOptions): string {
  return new Date(epochMs).toLocaleString(LOCALE, {
    timeZone: DISPLAY_TIME_ZONE,
    ...options,
  });
}

/** Saat:dakika:saniye (Türkiye saati). */
export function time(epochMs: number | null | undefined): string {
  if (!epochMs) return "—";
  return fmt(epochMs, { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

/** Yalnizca saat:dakika - mum kapanis saatleri icin (Türkiye saati). */
export function hourMinute(epochMs: number | null | undefined): string {
  if (!epochMs) return "—";
  return fmt(epochMs, { hour: "2-digit", minute: "2-digit" });
}

/** Gün/ay saat:dakika (Türkiye saati). */
export function dateTime(epochMs: number | null | undefined): string {
  if (!epochMs) return "—";
  return fmt(epochMs, {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Gün/ay/YIL saat:dakika (Türkiye saati) - `dateTime`in yil ICEREN hali.
 *
 * YALNIZCA uyelik/referans kodu tarihleri gibi AYLAR SONRA bile anlamli
 * kalması gereken tarihler icin kullanilir (kullanici istegi: "yıl dahil
 * gösterilsin... sadece 22/03 22:14 gibi yıl bilgisi olmayan format
 * kullanılmasın"). `dateTime` GENEL kullanimda (sinyal/denetim kaydi vb.)
 * KASITLI OLARAK yilsiz kalir - o baglamda zaman her zaman "bugun/bu
 * hafta" oldugu icin yil gurultudur; burasi ayri tutulur.
 */
export function dateTimeWithYear(epochMs: number | null | undefined): string {
  if (!epochMs) return "—";
  return fmt(epochMs, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Aynı anın UTC karşılığı - denetlenebilirlik için (detay ekranı). */
export function utcHourMinute(epochMs: number | null | undefined): string {
  if (!epochMs) return "—";
  return new Date(epochMs).toLocaleString(LOCALE, {
    timeZone: "UTC",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** "3 sn once" gibi goreli sure (saat diliminden bagimsiz). */
export function ago(epochMs: number | null | undefined): string {
  if (!epochMs) return "—";
  const seconds = Math.max(0, Math.round((Date.now() - epochMs) / 1000));
  if (seconds < 60) return `${seconds} sn önce`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} dk önce`;
  return `${Math.round(minutes / 60)} sa önce`;
}

export function duration(ms: number): string {
  return ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} sn`;
}

/**
 * Bir pozisyonun ACIK KALDIGI sure - GERCEK zaman (bar sayisi DEGIL).
 *
 * "X bar" farkli zaman dilimlerinde karsilastirilamaz (15m'de 8 bar = 2
 * saat, 4h'te 8 bar = 32 saat) - bu oturumda defalarca vurgulanan bir
 * ilke. Iki gercek zaman damgasi arasindaki farktan hesaplanir.
 */
export function holdDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return "—";
  const minutes = Math.round(ms / 60_000);
  if (minutes < 60) return `${minutes} dk`;
  const hours = Math.floor(minutes / 60);
  const remMinutes = minutes % 60;
  if (hours < 24) return remMinutes > 0 ? `${hours} sa ${remMinutes} dk` : `${hours} sa`;
  const days = Math.floor(hours / 24);
  const remHours = hours % 24;
  return remHours > 0 ? `${days} gün ${remHours} sa` : `${days} gün`;
}

/** Skor rengi: 80+ yesil, 65+ mavi, 50+ sari, altinda gri. */
export function scoreTone(score: number): string {
  if (score >= 80) return "var(--up)";
  if (score >= 65) return "var(--info)";
  if (score >= 50) return "var(--warn)";
  return "var(--neutral)";
}
